import { useState } from 'react';
import { ListFilter } from 'lucide-react';

import { Badge } from '@/components/Badge/Badge';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import {
    Table,
    TableBody,
    TableCell,
    TableCellStack,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { type AppUsageReport } from '@/features/reports/api';
import { ago, dayMonth, standingVariant } from '@/features/reports/app/labels';
import { APP_USAGE_COPY as COPY } from '@/features/reports/copy';
import { ReportCard } from '@/features/reports/parts';

/**
 * People on the app (ALS-160): one line per person, newest first, not one per sign-in. Below 768
 * each line becomes a card, the person leading with the version badge beside them and the device,
 * sign-ins and last seen each on a labelled line, so nothing sits behind a sideways swipe. The
 * name search narrows the list on the screen: the window's people are already all here.
 */
export function PeopleCard({ report }: { report: AppUsageReport }) {
    const [query, setQuery] = useState('');
    const needle = query.trim().toLowerCase();
    const shown = needle
        ? report.people.filter((p) => p.name.toLowerCase().includes(needle))
        : report.people;
    const columns = COPY.list.columns;

    return (
        <ReportCard
            title={COPY.list.title}
            subtitle={COPY.list.subtitle(shown.length)}
            actions={
                <SearchInput
                    size="sm"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={COPY.list.search}
                    aria-label={COPY.list.search}
                    className="w-full sm:w-[220px]"
                />
            }
        >
            {shown.length === 0 ? (
                needle ? (
                    <EmptyState
                        icon={<ListFilter />}
                        title={COPY.list.noMatch.title}
                        body={COPY.list.noMatch.body}
                    />
                ) : (
                    <p className="text-muted-foreground py-4 text-sm">{COPY.nobodyOpened}</p>
                )
            ) : (
                <Table stack>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{columns.person}</TableHead>
                            <TableHead>{columns.device}</TableHead>
                            <TableHead>{columns.app}</TableHead>
                            <TableHead className="text-right">{columns.signIns}</TableHead>
                            <TableHead>{columns.lastSeen}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {shown.map((p) => (
                            <TableRow key={p.userId}>
                                <TableCell data-stack="title" className="font-medium">
                                    <TableCellStack>
                                        <span>{p.name}</span>
                                        {p.email ? (
                                            <span className="text-muted-foreground font-normal break-all">
                                                {p.email}
                                            </span>
                                        ) : null}
                                    </TableCellStack>
                                </TableCell>
                                <TableCell data-stack="meta" data-label={columns.device}>
                                    {p.device}
                                </TableCell>
                                <TableCell data-stack="status">
                                    <Badge variant={standingVariant(p.standing)} size="sm">
                                        {p.version ?? COPY.versions.unknown}
                                    </Badge>
                                </TableCell>
                                <TableNum data-stack="meta" data-label={columns.signIns}>
                                    {p.signIns}
                                </TableNum>
                                <TableCell data-stack="meta" data-label={columns.lastSeen}>
                                    {p.lastSeenAt ? (
                                        <TableCellStack>
                                            <span>{dayMonth(p.lastSeenAt)}</span>
                                            <span className="text-muted-foreground text-xs">
                                                {COPY.list.lastAction(
                                                    ago(p.lastSeenAt, report.to),
                                                    p.lastAction,
                                                )}
                                            </span>
                                        </TableCellStack>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </ReportCard>
    );
}
